import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import tokenManager from '../utils/tokenManager';
import { Package, Plus, Edit2, Trash2, Save, X, Copy, Scissors, ChevronUp, ChevronDown, Download, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Move FlightCaseCard outside to prevent recreation
const FlightCaseCard = memo(({ 
  flightCase, 
  updateFlightCase, 
  updateItem, 
  deleteFlightCase, 
  deleteItem, 
  addItemToFlightCase,
  copyItems,
  cutItems,
  moveItemUp,
  moveItemDown,
  handleExcelPaste,
  isMobile 
}) => {
  console.log('FlightCaseCard rendering for:', flightCase.id);
  const flightCaseTotal = (parseFloat(flightCase.quantity) || 0) * (parseFloat(flightCase.value) || 0);
  const itemsTotal = flightCase.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  const grandTotal = flightCaseTotal + itemsTotal;
  const pasteAreaRef = useRef(null);

  return (
    <div style={{
      backgroundColor: 'white',
      border: '2px solid #007bff',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '20px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }}>
      {/* Flight Case Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #dee2e6'
      }}>
        <h3 style={{
          margin: 0,
          color: '#007bff',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          {flightCase.name}
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#28a745'
          }}>
            {Math.round(grandTotal).toLocaleString('no-NO')} NOK
          </div>
          <button
            onClick={() => deleteFlightCase(flightCase.id)}
            style={{
              padding: '6px',
              border: 'none',
              background: '#dc3545',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Delete flight case"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      {/* Paste Area */}
      <div style={{ marginBottom: '16px' }}>
        <div 
          ref={pasteAreaRef}
          onPaste={(e) => {
            e.preventDefault();
            const pasteData = e.clipboardData.getData('text');
            if (pasteData.includes('\t') || pasteData.includes('\n')) {
              handleExcelPaste(flightCase.id, pasteData);
            }
          }}
          style={{
            border: '2px dashed #28a745',
            borderRadius: '6px',
            padding: '12px',
            backgroundColor: '#f8fff9',
            textAlign: 'center',
            color: '#28a745',
            fontSize: '14px',
            cursor: 'pointer'
          }}
          onClick={() => pasteAreaRef.current?.focus()}
          tabIndex={0}
        >
          Paste Excel data (Ctrl+V) — format: Description | Quantity | Value
        </div>
      </div>

      {/* Items Table */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              {/* Flight Case Details Header */}
              <tr style={{ backgroundColor: '#f0f8ff' }}>
                <td colSpan="6" style={{ 
                  padding: '12px', 
                  border: '1px solid #b3d9ff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#666'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr', 
                    gap: '12px', 
                    alignItems: 'center' 
                  }}>
                    <input
                      key={`name-${flightCase.id}`}
                      type="text"
                      value={flightCase.name}
                      onChange={(e) => updateFlightCase(flightCase.id, 'name', e.target.value)}
                      style={{
                        border: '1px solid #ddd',
                        padding: '6px',
                        fontSize: '14px',
                        borderRadius: '4px'
                      }}
                      placeholder="Flight case name..."
                    />
                    <input
                      key={`qty-${flightCase.id}`}
                      type="number"
                      value={flightCase.quantity}
                      onChange={(e) => updateFlightCase(flightCase.id, 'quantity', e.target.value)}
                      style={{
                        border: '1px solid #ddd',
                        padding: '6px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                      placeholder="Qty"
                    />
                    <input
                      key={`value-${flightCase.id}`}
                      type="number"
                      value={flightCase.value}
                      onChange={(e) => updateFlightCase(flightCase.id, 'value', e.target.value)}
                      style={{
                        border: '1px solid #ddd',
                        padding: '6px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        textAlign: 'right'
                      }}
                      placeholder="Value NOK"
                    />
                    <div style={{ 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#28a745',
                      textAlign: 'right',
                      padding: '6px'
                    }}>
                      {Math.round(flightCaseTotal)} NOK
                    </div>
                  </div>
                </td>
              </tr>
              {/* Column Headers */}
              {flightCase.items.length > 0 && (
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', width: '80px' }}>Move</th>
                  <th style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', width: '80px' }}>Actions</th>
                  <th style={{ padding: '6px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Description</th>
                  <th style={{ padding: '6px', textAlign: 'center', borderBottom: '1px solid #dee2e6', width: '70px' }}>Qty</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', width: '90px' }}>Value NOK</th>
                  <th style={{ padding: '6px', textAlign: 'right', borderBottom: '1px solid #dee2e6', width: '90px' }}>Total NOK</th>
                </tr>
              )}
            </thead>
            <tbody>
              {flightCase.items.map((item, index) => (
                <tr 
                  key={item.id} 
                  style={{ 
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                      <button
                        onClick={() => moveItemUp(flightCase.id, index)}
                        disabled={index === 0}
                        style={{
                          padding: '2px',
                          border: 'none',
                          background: index === 0 ? '#e9ecef' : '#28a745',
                          color: index === 0 ? '#6c757d' : 'white',
                          borderRadius: '2px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '10px'
                        }}
                        title="Move up"
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        onClick={() => moveItemDown(flightCase.id, index)}
                        disabled={index === flightCase.items.length - 1}
                        style={{
                          padding: '2px',
                          border: 'none',
                          background: index === flightCase.items.length - 1 ? '#e9ecef' : '#28a745',
                          color: index === flightCase.items.length - 1 ? '#6c757d' : 'white',
                          borderRadius: '2px',
                          cursor: index === flightCase.items.length - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '10px'
                        }}
                        title="Move down"
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                      <button
                        onClick={() => copyItems(flightCase.id, [item.id])}
                        style={{
                          padding: '2px',
                          border: 'none',
                          background: '#17a2b8',
                          color: 'white',
                          borderRadius: '2px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                        title="Copy item"
                      >
                        <Copy size={10} />
                      </button>
                      <button
                        onClick={() => cutItems(flightCase.id, [item.id])}
                        style={{
                          padding: '2px',
                          border: 'none',
                          background: '#ffc107',
                          color: 'white',
                          borderRadius: '2px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                        title="Cut item"
                      >
                        <Scissors size={10} />
                      </button>
                      <button
                        onClick={() => deleteItem(flightCase.id, item.id)}
                        style={{
                          padding: '2px',
                          border: 'none',
                          background: '#dc3545',
                          color: 'white',
                          borderRadius: '2px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                        title="Delete item"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '4px' }}>
                    <input
                      key={`desc-${item.id}`}
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(flightCase.id, item.id, 'description', e.target.value)}
                      style={{
                        width: '100%',
                        border: 'none',
                        padding: '4px',
                        fontSize: '14px',
                        backgroundColor: 'transparent'
                      }}
                      placeholder="Item description..."
                    />
                  </td>
                  <td style={{ padding: '4px' }}>
                    <input
                      key={`qty-${item.id}`}
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(flightCase.id, item.id, 'quantity', e.target.value)}
                      style={{
                        width: '100%',
                        border: 'none',
                        padding: '4px',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        textAlign: 'center'
                      }}
                      placeholder="0"
                    />
                  </td>
                  <td style={{ padding: '4px' }}>
                    <input
                      key={`val-${item.id}`}
                      type="number"
                      value={item.value}
                      onChange={(e) => updateItem(flightCase.id, item.id, 'value', e.target.value)}
                      style={{
                        width: '100%',
                        border: 'none',
                        padding: '4px',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        textAlign: 'right'
                      }}
                      placeholder="0"
                    />
                  </td>
                  <td style={{ 
                    padding: '4px', 
                    fontWeight: 'bold',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    fontSize: '14px',
                    textAlign: 'right'
                  }}>
                    {parseFloat(item.total) > 0 ? Math.round(parseFloat(item.total)) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Item Button */}
      <button
        onClick={() => addItemToFlightCase(flightCase.id)}
        style={{
          padding: '10px 16px',
          border: '1px dashed #007bff',
          backgroundColor: 'transparent',
          color: '#007bff',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Plus size={16} />
        Add Item to Flight Case
      </button>
    </div>
  );
});

function SimpleInventoryView({ onBack }) {
  const [savedSheets, setSavedSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSheet, setEditingSheet] = useState(null);
  const [newSheetName, setNewSheetName] = useState('');
  const [flightCases, setFlightCases] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [clipboard, setClipboard] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const pasteAreaRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSavedSheets();
  }, []);

  const loadSavedSheets = async () => {
    try {
      const response = await fetch('/api/inventory/sheets', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded sheets:', data);
        setSavedSheets(data.sheets || []);
      } else {
        setSavedSheets([]);
      }
    } catch (error) {
      console.error('Error loading sheets:', error);
      setSavedSheets([]);
    }
  };

  const createNewSheet = async () => {
    if (!newSheetName.trim()) {
      toast.error('Sheet name is required');
      return;
    }

    try {
      const response = await fetch('/api/inventory/sheets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          name: newSheetName,
          data: []
        })
      });

      if (response.ok) {
        const newSheet = await response.json();
        setSavedSheets([...savedSheets, newSheet]);
        setNewSheetName('');
        setShowAddForm(false);
        // Don't auto-select the new sheet, let user click on it
        toast.success('Sheet created successfully');
      }
    } catch (error) {
      console.error('Error creating sheet:', error);
      toast.error('Failed to create sheet');
    }
  };

  const selectSheet = async (sheet) => {
    // Clear current data first
    setFlightCases([]);
    setHasUnsavedChanges(false);
    setSelectedSheet(sheet);
    
    // Load flight cases from database
    try {
      console.log('Loading sheet:', sheet.id);
      const response = await fetch(`/api/inventory/sheets/${sheet.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        const sheetData = await response.json();
        console.log('Loaded sheet data:', sheetData);
        
        // If no data, start with empty flight case
        if (!sheetData.data || sheetData.data.length === 0) {
          setFlightCases([{
            id: Date.now(),
            name: 'New Flight Case',
            description: '',
            quantity: 1,
            value: 0,
            items: []
          }]);
        } else {
          // Convert database format to flight case format
          const convertedFlightCases = convertInventoryToFlightCases(sheetData.data);
          console.log('Converted to flight cases:', convertedFlightCases);
          setFlightCases(convertedFlightCases);
        }
        setHasUnsavedChanges(false);
      } else {
        console.error('Failed to load sheet:', response.status);
        setFlightCases([]);
      }
    } catch (error) {
      console.error('Error loading sheet data:', error);
      setFlightCases([]);
    }
  };

  const deleteSheet = async (sheetId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this sheet?')) {
      return;
    }

    try {
      const response = await fetch(`/api/inventory/sheets/${sheetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        setSavedSheets(savedSheets.filter(s => s.id !== sheetId));
        if (selectedSheet?.id === sheetId) {
          setSelectedSheet(null);
          setFlightCases([]);
        }
        toast.success('Sheet deleted');
      }
    } catch (error) {
      console.error('Error deleting sheet:', error);
      toast.error('Failed to delete sheet');
    }
  };

  const exportToCSV = async (sheet, e) => {
    e.stopPropagation(); // Prevent sheet selection
    
    try {
      const response = await fetch(`/api/inventory/sheets/${sheet.id}/export/csv`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        // Get the CSV content
        const csvContent = await response.text();
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${sheet.name}_inventory.csv`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('CSV exported successfully');
      } else {
        throw new Error('Failed to export CSV');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const exportToCarnet = async (sheet, e) => {
    e.stopPropagation(); // Prevent sheet selection
    
    try {
      const response = await fetch(`/api/inventory/sheets/${sheet.id}/export/carnet`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (response.ok) {
        // Get the CSV content
        const csvContent = await response.text();
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${sheet.name}_ATA_carnet.csv`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('ATA Carnet CSV exported successfully');
      } else {
        throw new Error('Failed to export ATA Carnet CSV');
      }
    } catch (error) {
      console.error('Error exporting ATA Carnet CSV:', error);
      toast.error('Failed to export ATA Carnet CSV');
    }
  };

  const addFlightCase = () => {
    const newFlightCase = {
      id: Date.now(),
      name: 'New Flight Case',
      description: '',
      quantity: 1,
      value: 0,
      items: []
    };
    setFlightCases([...flightCases, newFlightCase]);
    setHasUnsavedChanges(true);
  };


  const updateFlightCase = useCallback((flightCaseId, field, value) => {
    setFlightCases(prevFlightCases => prevFlightCases.map(fc => 
      fc.id === flightCaseId ? { ...fc, [field]: value } : fc
    ));
    setHasUnsavedChanges(true);
  }, []);

  const deleteFlightCase = (flightCaseId) => {
    setFlightCases(flightCases.filter(fc => fc.id !== flightCaseId));
  };

  const addItemToFlightCase = (flightCaseId) => {
    const newItem = {
      id: Date.now() + Math.random(),
      description: '',
      quantity: '',
      value: '',
      total: 0
    };

    setFlightCases(flightCases.map(fc => 
      fc.id === flightCaseId 
        ? { ...fc, items: [...fc.items, newItem] }
        : fc
    ));
    setHasUnsavedChanges(true);
  };

  const updateItem = useCallback((flightCaseId, itemId, field, value) => {
    setFlightCases(prevFlightCases => prevFlightCases.map(fc => 
      fc.id === flightCaseId 
        ? {
            ...fc, 
            items: fc.items.map(item => {
              if (item.id === itemId) {
                const updatedItem = { ...item, [field]: value };
                // Recalculate total
                if (field === 'quantity' || field === 'value') {
                  const qty = parseInt(updatedItem.quantity) || 0;
                  const val = parseInt(updatedItem.value) || 0;
                  updatedItem.total = qty * val;
                }
                return updatedItem;
              }
              return item;
            })
          }
        : fc
    ));
    setHasUnsavedChanges(true);
  }, []);

  const deleteItem = (flightCaseId, itemId) => {
    setFlightCases(flightCases.map(fc => 
      fc.id === flightCaseId 
        ? { ...fc, items: fc.items.filter(item => item.id !== itemId) }
        : fc
    ));
  };

  const copyItems = (flightCaseId, itemIds) => {
    const flightCase = flightCases.find(fc => fc.id === flightCaseId);
    if (flightCase) {
      const itemsToCopy = flightCase.items.filter(item => itemIds.includes(item.id));
      setClipboard(itemsToCopy.map(item => ({ ...item, id: Date.now() + Math.random() })));
      toast.success(`${itemsToCopy.length} item(s) copied`);
    }
  };

  const cutItems = (flightCaseId, itemIds) => {
    const flightCase = flightCases.find(fc => fc.id === flightCaseId);
    if (flightCase) {
      const itemsToCut = flightCase.items.filter(item => itemIds.includes(item.id));
      setClipboard(itemsToCut.map(item => ({ ...item, id: Date.now() + Math.random() })));
      
      // Remove cut items from original location
      setFlightCases(flightCases.map(fc => 
        fc.id === flightCaseId 
          ? { ...fc, items: fc.items.filter(item => !itemIds.includes(item.id)) }
          : fc
      ));
      toast.success(`${itemsToCut.length} item(s) cut`);
    }
  };

  const pasteItems = (flightCaseId) => {
    if (clipboard.length === 0) {
      toast.error('No items to paste');
      return;
    }

    const newItems = clipboard.map(item => ({
      ...item,
      id: Date.now() + Math.random()
    }));

    setFlightCases(flightCases.map(fc => 
      fc.id === flightCaseId 
        ? { ...fc, items: [...fc.items, ...newItems] }
        : fc
    ));
    toast.success(`${newItems.length} item(s) pasted`);
  };

  const handleExcelPaste = (flightCaseId, pasteData) => {
    try {
      const lines = pasteData.trim().split('\n');
      const newItems = [];

      lines.forEach((line, index) => {
        const columns = line.split('\t').map(col => col.trim());
        if (columns.length >= 1 && columns[0]) {
          const description = columns[0] || '';
          const quantity = parseFloat(columns[1]) || '';
          const value = parseFloat(columns[2]) || '';
          const total = quantity && value ? Math.round(quantity * value) : 0;

          newItems.push({
            id: Date.now() + Math.random() + index,
            description,
            quantity: quantity || '',
            value: value || '',
            total
          });
        }
      });

      if (newItems.length > 0) {
        setFlightCases(flightCases.map(fc => 
          fc.id === flightCaseId 
            ? { ...fc, items: [...fc.items, ...newItems] }
            : fc
        ));
        toast.success(`${newItems.length} item(s) pasted from Excel`);
      }
    } catch (error) {
      console.error('Error parsing Excel data:', error);
      toast.error('Error pasting Excel data');
    }
  };

  const moveItemUp = (flightCaseId, index) => {
    if (index === 0) return;
    setFlightCases(flightCases.map(fc => {
      if (fc.id === flightCaseId) {
        const newItems = [...fc.items];
        [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
        return { ...fc, items: newItems };
      }
      return fc;
    }));
  };

  const moveItemDown = (flightCaseId, index) => {
    setFlightCases(flightCases.map(fc => {
      if (fc.id === flightCaseId) {
        if (index === fc.items.length - 1) return fc;
        const newItems = [...fc.items];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        return { ...fc, items: newItems };
      }
      return fc;
    }));
  };

  // Convert database inventory format to flight cases
  const convertInventoryToFlightCases = (inventoryData) => {
    console.log('Converting inventory data:', inventoryData);
    
    // If no data, return empty array
    if (!inventoryData || inventoryData.length === 0) {
      return [];
    }

    const flightCases = [];
    let currentFlightCase = null;

    // NEW LOGIC: Use database fields is_case and parent_case_id
    const caseMap = new Map();
    
    // First pass: Create all flight cases
    inventoryData.forEach((row, index) => {
      const hasDescription = row.description && row.description.trim() !== '';
      
      if (!hasDescription) {
        return; // Skip empty rows
      }

      // If is_case is true, this is a flight case
      if (row.is_case) {
        const flightCase = {
          id: row.id || Date.now() + index,
          name: row.description,
          description: row.description,
          quantity: parseInt(row.quantity) || 1,
          value: parseInt(row.valueNOK) || parseInt(row.value) || 0,
          items: []
        };
        caseMap.set(row.id, flightCase);
        flightCases.push(flightCase);
        console.log('Created flight case:', row.description);
      }
    });
    
    // Second pass: Add items to their parent cases
    inventoryData.forEach((row, index) => {
      const hasDescription = row.description && row.description.trim() !== '';
      
      if (!hasDescription || row.is_case) {
        return; // Skip empty rows and cases (already processed)
      }

      // This is an item - find its parent case
      const parentCase = caseMap.get(row.parent_case_id);
      if (parentCase) {
        parentCase.items.push({
          id: row.id || Date.now() + Math.random() + index,
          description: row.description,
          quantity: row.quantity || '',
          value: parseInt(row.valueNOK) || parseInt(row.value) || 0,
          total: (row.quantity && row.valueNOK) ? Math.round(parseInt(row.quantity) * parseInt(row.valueNOK)) : 0
        });
        console.log('Added item to', parentCase.name + ':', row.description);
      } else {
        // Orphaned item - create a standalone case for it
        const orphanCase = {
          id: row.id || Date.now() + index,
          name: row.description,
          description: row.description,
          quantity: parseInt(row.quantity) || 1,
          value: parseInt(row.valueNOK) || parseInt(row.value) || 0,
          items: []
        };
        flightCases.push(orphanCase);
        console.log('Created standalone case for orphaned item:', row.description);
      }
    });

    console.log('Converted flight cases:', flightCases);
    return flightCases;
  };

  // Convert flight cases back to database format with proper parent-child relationships
  const convertFlightCasesToInventory = (flightCases) => {
    const inventoryData = [];

    flightCases.forEach((flightCase) => {
      // Add flight case as first row with is_case = true
      inventoryData.push({
        id: flightCase.id,
        description: flightCase.name,
        quantity: String(flightCase.quantity || 1),
        valueNOK: String(flightCase.value || 0),
        total: String(Math.round((flightCase.quantity || 1) * (flightCase.value || 0))),
        is_case: true,
        parent_case_id: null
      });

      // Add items with parent_case_id pointing to this flight case
      if (flightCase.items && flightCase.items.length > 0) {
        flightCase.items.forEach(item => {
          inventoryData.push({
            id: item.id,
            description: item.description || '',
            quantity: String(item.quantity || ''),
            valueNOK: String(item.value || ''),
            total: String(Math.round(item.total || 0)),
            is_case: false,
            parent_case_id: flightCase.id
          });
        });
      }
    });

    console.log('Converting flight cases to inventory:', { flightCases, inventoryData });
    return inventoryData;
  };

  // Save to database
  const saveToDatabase = async () => {
    if (!selectedSheet) {
      console.log('No sheet selected');
      return;
    }

    try {
      const inventoryData = convertFlightCasesToInventory(flightCases);
      console.log('Saving data:', {
        sheetId: selectedSheet.id,
        itemCount: inventoryData.length,
        inventoryData: inventoryData.slice(0, 3) // Log first 3 items for debugging
      });
      
      const response = await fetch(`/api/inventory/sheets/${selectedSheet.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({
          name: selectedSheet.name,
          data: inventoryData
        })
      });

      let responseData;
      try {
        const responseText = await response.text();
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse JSON response:', responseText);
          responseData = { error: responseText };
        }
      } catch (e) {
        console.error('Failed to read response:', e);
        responseData = { error: 'Failed to read response' };
      }
      
      console.log('Save response:', response.status, responseData);

      if (response.ok) {
        toast.success('Data saved successfully');
        setHasUnsavedChanges(false);
        
        // Reload the sheet to confirm data was saved
        await selectSheet(selectedSheet);
      } else {
        console.error('Save failed:', response.status, responseData);
        toast.error(`Failed to save data: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error('Error saving data');
    }
  };

  // Remove auto-save for now as it's causing focus issues
  // Manual save only with the Save button

  // FlightCaseCard moved outside component

  return (
    <div style={{ 
      display: 'flex', 
      height: '100vh', 
      backgroundColor: '#f5f5f5',
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      {/* Sidebar */}
      <div style={{
        width: isMobile ? '100%' : '300px',
        backgroundColor: 'white',
        borderRight: isMobile ? 'none' : '1px solid #dee2e6',
        borderBottom: isMobile ? '1px solid #dee2e6' : 'none',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isMobile ? '200px' : '100vh',
        overflowY: 'auto'
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <Package size={24} style={{ color: '#007bff' }} />
            <h2 style={{ margin: 0, color: '#007bff' }}>Inventory Sheets</h2>
          </div>
          
          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px dashed #007bff',
                backgroundColor: 'transparent',
                color: '#007bff',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px'
              }}
            >
              <Plus size={16} />
              New Sheet
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                type="text"
                value={newSheetName}
                onChange={(e) => setNewSheetName(e.target.value)}
                placeholder="Sheet name..."
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px'
                }}
                onKeyPress={(e) => e.key === 'Enter' && createNewSheet()}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={createNewSheet}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: 'none',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                  }}
                >
                  <Save size={14} />
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewSheetName('');
                  }}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: 'none',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                  }}
                >
                  <X size={14} />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, padding: '10px' }}>
          {savedSheets.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
              <Package size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
              <p>No sheets yet</p>
            </div>
          ) : (
            savedSheets.map(sheet => (
              <div
                key={sheet.id}
                onClick={() => selectSheet(sheet)}
                style={{
                  padding: '12px',
                  margin: '5px 0',
                  backgroundColor: selectedSheet?.id === sheet.id ? '#e3f2fd' : '#f8f9fa',
                  border: selectedSheet?.id === sheet.id ? '2px solid #2196f3' : '1px solid #dee2e6',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{ fontWeight: selectedSheet?.id === sheet.id ? 'bold' : 'normal' }}>
                  {sheet.name}
                </span>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    onClick={(e) => exportToCSV(sheet, e)}
                    style={{
                      padding: '6px',
                      border: 'none',
                      background: '#28a745',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Export to CSV"
                  >
                    <Download size={16} />
                  </button>
                  <button
                    onClick={(e) => exportToCarnet(sheet, e)}
                    style={{
                      padding: '6px',
                      border: 'none',
                      background: '#17a2b8',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Export ATA Carnet CSV"
                  >
                    <FileText size={16} />
                  </button>
                  <button
                    onClick={(e) => deleteSheet(sheet.id, e)}
                    style={{
                      padding: '6px',
                      border: 'none',
                      background: '#dc3545',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Delete sheet"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        padding: '20px', 
        overflowY: 'auto',
        maxHeight: isMobile ? 'calc(100vh - 200px)' : '100vh'
      }}>
        {!selectedSheet ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60%',
            flexDirection: 'column',
            color: '#6c757d'
          }}>
            <Package size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
            <h3>No Sheet Selected</h3>
            <p>Create a new sheet or select one from the sidebar to start managing your inventory.</p>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              paddingBottom: '20px',
              borderBottom: '2px solid #dee2e6'
            }}>
              <h1 style={{ margin: 0, color: '#333' }}>
                {selectedSheet.name}
              </h1>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={saveToDatabase}
                  style={{
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: hasUnsavedChanges ? '#dc3545' : '#28a745',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Save size={20} />
                  {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
                </button>
                <button
                  onClick={addFlightCase}
                  style={{
                    padding: '12px 20px',
                    border: 'none',
                    backgroundColor: '#007bff',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={20} />
                  Add Flight Case
                </button>
              </div>
            </div>

            {/* Total Value Display */}
            {flightCases.length > 0 && (
              <div style={{
                padding: '15px 20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px solid #007bff',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#007bff'
                }}>
                  <span>Total Inventory Value:</span>
                  <span>
                    {Math.round(flightCases.reduce((total, flightCase) => {
                      const caseValue = (flightCase.quantity || 1) * (flightCase.value || 0);
                      const itemsValue = flightCase.items.reduce((itemTotal, item) => {
                        const quantity = parseFloat(item.quantity) || 0;
                        const value = parseFloat(item.value) || 0;
                        return itemTotal + (quantity * value);
                      }, 0);
                      return total + caseValue + itemsValue;
                    }, 0)).toLocaleString('no-NO')} NOK
                  </span>
                </div>
              </div>
            )}

            {/* Flight Cases Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(500px, 1fr))',
              gap: '20px'
            }}>
              {flightCases.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '60px',
                  color: '#6c757d'
                }}>
                  <Package size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
                  <h3>No Flight Cases Yet</h3>
                  <p>Click "Add Flight Case" to create your first flight case.</p>
                </div>
              ) : (
                flightCases.map(flightCase => (
                  <FlightCaseCard 
                    key={flightCase.id} 
                    flightCase={flightCase}
                    updateFlightCase={updateFlightCase}
                    updateItem={updateItem}
                    deleteFlightCase={deleteFlightCase}
                    deleteItem={deleteItem}
                    addItemToFlightCase={addItemToFlightCase}
                    copyItems={copyItems}
                    cutItems={cutItems}
                    moveItemUp={moveItemUp}
                    moveItemDown={moveItemDown}
                    handleExcelPaste={handleExcelPaste}
                    isMobile={isMobile}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SimpleInventoryView;
